import { StudentsRemovalRequestDetailLoader } from './StudentsRemovalRequestDetailLoader';
import {
  Modal,
  DetailBox,
  Select,
  TextInput,
  TextAreaInput,
  Icon,
  Button,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { SecureResponseFile } from '../../../../components/AzureFilesUploader/types';
import ValidationFileChip from '../../../CourseValidationInternalStudent/parts/ModalCreateValidation/ValidationFileChip';
import { TranslationsKeys } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';

type Props = {
  studyPlanEnrollmentId: number;
  onClose: () => void;
};

export function StudentsRemovalRequestDetailModal({
  studyPlanEnrollmentId,
  onClose,
}: Props) {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_STUDENTS,
  );
  const texts = useMemo(() => {
    return t<
      string,
      {
        title: string;
        subTitle: string;
        studentColumn: string;
        idColumn: string;
        currentState: string;
        studyPlan: string;
        cause: string;
        description: string;
        complementaryFiles: string;
        approved: string;
        recieved: string;
        button: string;
      }
    >('detailModal', {
      returnObjects: true,
    });
  }, [t]);

  const handleDownloadFile = useCallback((file: SecureResponseFile) => {
    if (!file?.url) {
      return;
    }
    window.open(file?.url, '_blank');
  }, []);

  return (
    <Modal isOpen toggle={onClose} size="md">
      <StudentsRemovalRequestDetailLoader
        studyPlanEnrollmentId={studyPlanEnrollmentId}
      >
        {({ data }) => (
          <>
            <h1 className="text-dark text-center fw-600 fs-20">
              {texts.title}
            </h1>
            <p className=" text-center fs-16 mb-4">{texts.subTitle}</p>
            <DetailBox
              columns={[
                {
                  title: texts.studentColumn,
                  body: `${data.student.accountNames} ${data.student.accountLastNames}`,
                  size: { xs: 12, md: 8 },
                },
                {
                  title: texts.idColumn,
                  body: data.student.identificationValueFormatted,
                  size: { xs: 12, md: 4 },
                },
              ]}
            />
            <div className="my-3">
              <Icon name="information" className="mr-1" />
              <span>
                {texts.currentState}{' '}
                {data.removalRequest.status ? texts.approved : texts.recieved}
              </span>
            </div>

            <TextInput
              label={texts.studyPlan}
              name="studyPlan"
              disabled
              value={data.studyPlan}
            />
            <Select
              label={texts.cause}
              name="removalReason"
              disabled
              options={[]}
              value={{
                label: data.removalRequest.type,
                value: data.removalRequest.type,
              }}
            />
            <TextAreaInput
              label={texts.description}
              name="description"
              disabled
              value={data.removalRequest.comment}
            />
            {!!data.files.length && (
              <div
                className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
                style={{ gap: 5, boxShadow: 'inherit' }}
              >
                {texts.complementaryFiles}
                {data.files?.map((file: any, i: number) => (
                  <ValidationFileChip
                    key={file.id.toString()}
                    value={i}
                    children={`${file.name}.${file.extension}`}
                    onDownload={() => handleDownloadFile(file)}
                  />
                ))}
              </div>
            )}
            <Button
              className="mt-4"
              fullwidth
              text={texts.button}
              onClick={() => onClose()}
            />
          </>
        )}
      </StudentsRemovalRequestDetailLoader>
    </Modal>
  );
}
